/**
 * Health Incident Service
 * API service pro spravu zdravotnich incidentu rostlin
 */
import { apiClient } from './api';
import type {
  HealthIncident,
  CreateHealthIncidentDTO,
  UpdateHealthIncidentDTO,
  HealthIncidentFilters,
  AreaHealthSummary as HealthIncidentStats,
} from '../types/health-incidents';

const BASE_PATH = '/health-incidents';

/**
 * Ziska vsechny zdravotni incidenty s volitelnym filtrem
 */
export const getHealthIncidents = async (
  filters?: HealthIncidentFilters,
): Promise<HealthIncident[]> => {
  // Backend routes: GET /health-incidents/area/:areaId or GET /health-incidents/open etc.
  // Use area-specific route when growing_area_id is provided
  if (filters?.growing_area_id) {
    const response = await apiClient.get<HealthIncident[]>(
      `${BASE_PATH}/area/${filters.growing_area_id}`,
    );
    // Client-side filtering for additional criteria
    let result = Array.isArray(response) ? response : [];
    if (filters.status) result = result.filter(i => i.status === filters.status);
    if (filters.incident_type) result = result.filter(i => i.incident_type === filters.incident_type);
    if (filters.severity) result = result.filter(i => i.severity >= filters.severity!);
    return result;
  }

  // Fallback: GET /health-incidents/open for open incidents
  if (filters?.status === 'open') {
    const response = await apiClient.get<HealthIncident[]>(`${BASE_PATH}/open`);
    return Array.isArray(response) ? response : [];
  }

  // No specific filter — try overview endpoint
  try {
    const response = await apiClient.get<HealthIncident[]>(`${BASE_PATH}/open`);
    return Array.isArray(response) ? response : [];
  } catch {
    return [];
  }
};

/**
 * Ziska konkretni zdravotni incident podle ID
 */
export const getHealthIncident = async (id: string): Promise<HealthIncident> => {
  return apiClient.get<HealthIncident>(`${BASE_PATH}/${id}`);
};

/**
 * Vytvori novy zdravotni incident
 */
export const createHealthIncident = async (
  data: CreateHealthIncidentDTO,
): Promise<HealthIncident> => {
  return apiClient.post<HealthIncident>(BASE_PATH, data);
};

/**
 * Aktualizuje existujici zdravotni incident
 */
export const updateHealthIncident = async (
  id: string,
  data: UpdateHealthIncidentDTO,
): Promise<HealthIncident> => {
  return apiClient.patch<HealthIncident>(`${BASE_PATH}/${id}`, data);
};

/**
 * Smaze zdravotni incident
 */
export const deleteHealthIncident = async (id: string): Promise<void> => {
  await apiClient.delete(`${BASE_PATH}/${id}`);
};

/**
 * Vyresi zdravotni incident
 */
export const resolveHealthIncident = async (
  id: string,
  resolution_notes: string,
): Promise<HealthIncident> => {
  return apiClient.post<HealthIncident>(`${BASE_PATH}/${id}/resolve`, {
    resolution_notes,
  });
};

/**
 * Ziska statistiky zdravotnich incidentu pro oblast
 */
export const getHealthIncidentStats = async (
  growing_area_id: string,
): Promise<HealthIncidentStats> => {
  return apiClient.get<HealthIncidentStats>(`${BASE_PATH}/area/${growing_area_id}/summary`);
};

export const healthIncidentService = {
  getHealthIncidents, // This one is not used directly usually, but keep it
  getAll: getHealthIncidents, // Alias for consistency if needed
  getIncidentsByArea: (areaId: string, filters?: HealthIncidentFilters) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.incident_type) params.append('type', filters.incident_type);
    if (filters?.severity) params.append('severity_min', filters.severity.toString());
    const qs = params.toString();
    return apiClient.get<HealthIncident[]>(`${BASE_PATH}/area/${areaId}${qs ? `?${qs}` : ''}`);
  },

  getHealthIncident,
  getById: getHealthIncident, // Hook uses getById

  createHealthIncident,
  create: createHealthIncident, // Hook uses create

  updateHealthIncident,
  update: updateHealthIncident, // Hook uses update

  deleteHealthIncident,
  delete: deleteHealthIncident, // Hook uses delete

  resolveHealthIncident,
  resolve: resolveHealthIncident, // Hook uses resolve

  getHealthIncidentStats,
  getAreaSummary: getHealthIncidentStats, // Hook uses getAreaSummary

  // Hook uses getOpenIncidents -> likely a filter on getHealthIncidents
  getOpenIncidents: (areaId?: string) =>
    // Use the dedicated /open endpoint if available, but filter works too.
    // For area-specific open incidents, use /area/:areaId/open if available
    areaId ? apiClient.get<HealthIncident[]>(`${BASE_PATH}/area/${areaId}/open`) : apiClient.get<HealthIncident[]>(`${BASE_PATH}/open`),

  // Hook uses getSystemOverview
  getSystemOverview: () => apiClient.get<HealthIncidentStats>(`${BASE_PATH}/overview`),
};
